/**
 * PATH: packages/auth/src/permission-types.ts
 * FILE_ID: AUTH-RBAC-TYPES-001
 * PURPOSE: RBAC type definitions and constants for the permission system
 * WHY: Extracted from usePermission.ts to keep files under 300 lines
 *      and separate data definitions from hook logic.
 * DEPENDENCIES:
 *   - None (pure types and constants)
 * RELATED:
 *   - AUTH-RBAC-STATE-001 (rbac-state.ts): useRBAC hook that produces RBACState
 *   - packages/auth/src/usePermission.ts: hooks that consume these types
 */

// =============================================================================
// TYPES
// =============================================================================

export type PermissionAction = 'none' | 'read' | 'write' | 'delete' | 'admin'
export type PermissionScope = 'own' | 'team' | 'dept' | 'org' | 'global'

export interface ModulePermission {
  action: PermissionAction
  scope: PermissionScope
}

export interface UserPermissions {
  [module: string]: ModulePermission
}

export interface RBACState {
  /** Organisation id whose response produced this settled permission snapshot. */
  authorisedOrganisationId: string | null
  /** Current user's permissions by module */
  permissions: UserPermissions
  /** Whether user is a global admin */
  isGlobalAdmin: boolean
  /** Whether user is an org admin */
  isOrgAdmin: boolean
  /** User's role code (e.g., 'org_admin', 'project_manager') */
  role: string | null
  /** User's hierarchy level (lower = more privileged, 0 = global admin) */
  hierarchyLevel: number
  /** List of modules user can access */
  accessibleModules: string[]
  /** Capability toggles (orthogonal to tier; RBAC V2 MR 11/12). Owners +
   *  global admins implicitly hold all three. Surfaced from
   *  /api/rbac/permissions/ so finer Settings/CFO/Billing gates can read them
   *  directly instead of inferring from accessibleModules. */
  hasFinanceOrgWide: boolean
  isOrgAdminUser: boolean
  isBillingAdmin: boolean
  /** Org opt-in for /api/jobs/scope-groups/ (Quotech HQ default: off) */
  enableJobScopeGrouping: boolean
  /** Coarse onboarding access level ('full' | 'diary_only'). Drives the
   *  master sidebar clamp. Owners/global admins are always 'full'. Defaults
   *  to 'full' so a pre-preset backend degrades to "no restriction". */
  accessPreset: string
  /** Sidebar nav-item ids this preset may render, or null for no limit.
   *  e.g. ['diary','emails','calendar'] for diary_only. Null = show
   *  everything the role allows (the legacy/default behaviour). */
  navAllowlist: string[] | null
  /** Loading state */
  loading: boolean
  /** Error if fetch failed */
  error: string | null
  /** Refresh permissions */
  refresh: () => Promise<void>
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Permission action levels (higher = more access) */
export const ACTION_LEVELS: Record<PermissionAction, number> = {
  none: 0,
  read: 1,
  write: 2,
  delete: 3,
  admin: 4,
}

/** All available modules in Quotech */
export const MODULES = [
  'jobs',
  'cfo',
  'qa',
  'workforce',
  'crm',
  'qdrive',
  'email',
  'billing',
  'ai',
  'quote',
  'hr',
  'inventory',
  'assets',
  'teams',
  'settings',
] as const

export type Module = typeof MODULES[number]

// Re-export convenience alias
export type Permissions = UserPermissions
